import { useQuery } from '@tanstack/react-query';

import { bundlePath, fetchBundle } from '../bundles/downloadBundle';
import { workflowKeys } from '../queryKeys';

/**
 * A workflow's bundle as a file, for Copy to workspace (spec 208; spec 205).
 *
 * The bundle does not depend on where it is going, so it is read once, when the
 * first target is chosen, and reused for any other target the person picks:
 * reading it when the dialog opens would download a file the person may never
 * use, and reading it per target would download it again.
 */
export function useSourceBundle(
  workspaceId: string,
  workflow: { id: string; key: string },
  /** The agents and skills it carries, each sorted, so one choice is one entry. */
  sent: { agents: readonly string[]; skills: readonly string[] },
  enabled: boolean,
) {
  const query = useQuery({
    queryKey: workflowKeys.sourceBundle(workspaceId, workflow.id, sent),
    queryFn: async (): Promise<File> => {
      const blob = await fetchBundle(
        bundlePath(workspaceId, workflow.id, {
          agents: [...sent.agents],
          skills: [...sent.skills],
        }),
      );
      return new File([blob], `${workflow.key}.zip`, {
        type: 'application/zip',
      });
    },
    enabled,
    staleTime: Infinity,
    gcTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    file: query.data ?? null,
    reading: query.isFetching,
    failure: query.error,
  };
}
